import React, { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import './Toast.css';

/**
 * Toast Notification System
 * Usage:
 *   - toastRef.current?.show('Message', 'success' | 'error' | 'info')
 *   - toastRef.current?.showError('Error message')
 *   - toastRef.current?.showSuccess('Success message')
 * 
 * Hook Usage:
 *   const { toast, ToastContainer } = useToast();
 *   return <><ToastContainer />{content}</>
 */

// Context for toast
const ToastContext = createContext(null);

// Toast Provider Component
export function ToastProvider({ children }) {
  const toastRef = useRef();
  
  const toast = {
    success: (msg) => toastRef.current?.showSuccess(msg),
    error: (msg) => toastRef.current?.showError(msg),
    info: (msg) => toastRef.current?.showInfo(msg),
    show: (msg, type = 'info', duration = 3000) => toastRef.current?.show(msg, type, duration)
  };
  
  return (
    <ToastContext.Provider value={toast}>
      <Toast ref={toastRef} />
      {children}
    </ToastContext.Provider>
  );
}

// Hook to use toast
export function useToast() {
  const toastRef = useRef();

  const show = useCallback((msg, type = 'info', duration = 3000) => {
    return toastRef.current?.show(msg, type, duration);
  }, []);

  const success = useCallback((msg, duration = 3000) => {
    return toastRef.current?.showSuccess ? toastRef.current.showSuccess(msg, duration) : show(msg, 'success', duration);
  }, [show]);

  const error = useCallback((msg, duration = 4000) => {
    return toastRef.current?.showError ? toastRef.current.showError(msg, duration) : show(msg, 'error', duration);
  }, [show]);

  const warning = useCallback((msg, duration = 3500) => {
    return show(msg, 'warning', duration);
  }, [show]);

  const info = useCallback((msg, duration = 3000) => {
    return toastRef.current?.showInfo ? toastRef.current.showInfo(msg, duration) : show(msg, 'info', duration);
  }, [show]);

  const ToastContainer = useCallback(() => <Toast ref={toastRef} />, []);

  const toastMethods = { success, error, warning, info, show };

  return {
    ...toastMethods,
    toast: toastMethods,
    ToastContainer
  };
}

export const Toast = React.forwardRef((props, ref) => {
  const [toasts, setToasts] = useState([]);

  const show = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type };
    
    setToasts(prev => [...prev, newToast]);
    
    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }
    
    return id;
  }, []);

  React.useImperativeHandle(ref, () => ({
    show,
    showSuccess: (message, duration = 3000) => show(message, 'success', duration),
    showError: (message, duration = 4000) => show(message, 'error', duration),
    showInfo: (message, duration = 3000) => show(message, 'info', duration),
    dismiss: (id) => {
      setToasts(prev => prev.filter(t => t.id !== id));
    },
    
    clearAll: () => {
      setToasts([]);
    }
  }));

  return (
    <div className="toast-container">
      {toasts.map(toast => (
        <div key={toast.id} className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            {toast.type === 'success' && <span className="toast-icon">✓</span>}
            {toast.type === 'error' && <span className="toast-icon">✕</span>}
            {toast.type === 'info' && <span className="toast-icon">ℹ</span>}
            <span className="toast-message">{toast.message}</span>
          </div>
        </div>
      ))}
    </div>
  );
});

Toast.displayName = 'Toast';

export default Toast;
